import React from "react";
import { CompactTextField } from "./CompactTextField";
import { useFormContext } from "react-hook-form";

function useDebounce(props: {
  value: any;
  delay: number;
  callback: (value: any) => void;
}) {
  React.useEffect(() => {
    const timeout = setTimeout(() => props.callback(props.value), props.delay);
    return () => clearTimeout(timeout);
  }, [props.value]);
}

const is10DigitPhoneNumber = (value: string) => {
  value = value.replace(/\D/g, "");
  let length = value.length;
  return length === 10;
};

const isValidEmail = (value: string) => {
  value = value.trim();
  return value.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

export function EmailOrPhoneTextField(props: {
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  [key: string]: any;
}) {
  const { register, setValue, setError, formState, watch, clearErrors } =
    useFormContext();
  const { name, label, placeholder, value: propValue, ...restProps } = props;
  const fieldName = props.name || "emailOrPhoneNumber";
  const [isTouched, setIsTouched] = React.useState(false);

  useDebounce({
    value: watch(fieldName),
    delay: 400,
    callback: () => {
      if (!isTouched) return;
      const value = watch(fieldName);
      if (value?.length === 0) {
        return setError(fieldName, { message: "This field is required" });
      }
      const isNumber = /^\d+$/.test(value); // match digit characters
      if (!isNumber && !isValidEmail(value)) {
        return setError(fieldName, { message: "Please enter a valid email" });
      } else if (isNumber && !is10DigitPhoneNumber(value)) {
        return setError(fieldName, {message: "Please enter a valid phone number"}); // prettier-ignore
      } else {
        return clearErrors(fieldName);
      }
    },
  });

  const handleChange = (event: any) => {
    const value = event.target.value;
    const isNumber = /^\d+$/.test(value); // match digit characters

    setIsTouched(true);

    // Check if current value is already a 10-digit number
    if (isNumber && value.length > 10) {
      return setValue(fieldName, value?.slice(0, 10));
    }

    if (isNumber) {
      // For numbers, only allow up to 10 digits
      if (value.length <= 10) {
        setValue(fieldName, value);
      }
    } else {
      // For non-numbers (email), allow any input
      setValue(fieldName, value);
    }
  };

  return (
    <CompactTextField
      {...register(fieldName, {
        validate: () => {
          const value = watch(fieldName);
          const isNumber = /^\d+$/.test(value); // match digit characters
          if (!value?.length) {
            return "This field is required";
          } else if (isNumber && !is10DigitPhoneNumber(value)) {
            return "Please enter a valid phone number";
          } else if (!isNumber && !isValidEmail(value)) {
            return "Please enter a valid email";
          } else {
            return true;
          }
        },
      })}
      label={label}
      onChange={handleChange}
      error={!!formState.errors[fieldName]}
      helperText={formState.errors[fieldName]?.message as string}
      {...restProps}
    />
  );
}
